import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { getSocket } from '@/lib/socket';
import {
  getNotificationsListQueryKey,
  getNotificationsUnreadCountsQueryKey,
  getWithdrawalGetBalanceQueryKey,
  getWithdrawalListQueryKey,
  useNotificationsListHook,
} from '@/lib/endpoints';
import { useOpenNotificationDetail, useRingBell, useShowIncomingNotification } from '@/store/notificationsStore';
import { NotificationType } from '@/lib/models';
import { cueJumpLevel } from './jump-level';
import { useNotificationActions } from './useNotificationActions';

/** Backend emits this on the user's room when a notification is created (ingestion → RealtimePublisher). */
const NOTIFICATION_CREATED = 'notification:created';

/**
 * Notification types that move wallet state (funds reserved/returned + a row
 * leaving the pending-withdrawals list). Their arrival must refresh the wallet
 * caches too — the withdrawal list has no realtime channel of its own, so
 * without this a backend/admin-driven approve/reject leaves the "Pending
 * Requests" card stale until a manual refetch.
 */
const WALLET_REFRESH_TYPES = new Set<string>([NotificationType.WITHDRAW_APPROVED, NotificationType.WITHDRAW_FAILED]);

/** The cue payload the ingestion service publishes — an id + type + jump level, no copy. */
function cueNotificationId(payload: unknown): string | undefined {
  if (payload !== null && typeof payload === 'object' && 'notificationId' in payload) {
    const id = (payload as { notificationId: unknown }).notificationId;
    if (typeof id === 'string') return id;
  }
  return undefined;
}

/** The notification `type` carried on the cue (used to trigger domain-specific refreshes). */
function cueNotificationType(payload: unknown): string | undefined {
  if (payload !== null && typeof payload === 'object' && 'type' in payload) {
    const type = (payload as { type: unknown }).type;
    if (typeof type === 'string') return type;
  }
  return undefined;
}

/**
 * Keep the notification feed + unread counts fresh in realtime, and drive the
 * in-app arrival surface at the loudness the backend decided. Mirrors
 * {@link useKycRealtime}: listen on the user's Socket.IO room and invalidate the
 * relevant queries — no polling, the API stays the source of truth.
 *
 * The cue carries `{ notificationId, type, jumpLevel }` — no title/body. The
 * effective (post-cooldown) `jumpLevel` selects the surface:
 *  - `BELL`   — ring + invalidate only (no fetch, no banner, no pop-up).
 *  - `BANNER` — fetch the row and pop it in as the banner (rings via the store).
 *  - `POPUP`  — fetch the row, ring, and auto-open its detail pop-up.
 *
 * A missing/unknown jump level defaults to `BANNER` (today's behavior). Fetching
 * the row (banner/pop-up) is best-effort: a failed fetch just skips the surface,
 * while the invalidations above still refresh the bell badge + open panels.
 *
 * Mount once near the root, inside QueryClientProvider.
 */
export function useNotificationsRealtime(): void {
  const queryClient = useQueryClient();
  const fetchNotifications = useNotificationsListHook();
  const ring = useRingBell();
  const showIncoming = useShowIncomingNotification();
  const openDetail = useOpenNotificationDetail();
  // `markRead` is a fresh closure each render, so hold it in a ref rather than
  // an effect dep — otherwise the socket subscription would tear down and
  // re-attach on every render. Every other dep here is referentially stable.
  const { markRead } = useNotificationActions();
  const markReadRef = useRef(markRead);
  useEffect(() => {
    markReadRef.current = markRead;
  }, [markRead]);

  useEffect(() => {
    const socket = getSocket();
    const onCreated = (payload: unknown) => {
      void queryClient.invalidateQueries({ queryKey: getNotificationsListQueryKey() });
      void queryClient.invalidateQueries({ queryKey: getNotificationsUnreadCountsQueryKey() });

      // Wallet-moving notifications also refresh the wallet caches (balance +
      // pending-withdrawals list). Keyed off the cue's type so it fires at every
      // jump level — including BELL, which never fetches the row.
      const type = cueNotificationType(payload);
      if (type !== undefined && WALLET_REFRESH_TYPES.has(type)) {
        void queryClient.invalidateQueries({ queryKey: getWithdrawalListQueryKey() });
        void queryClient.invalidateQueries({ queryKey: getWithdrawalGetBalanceQueryKey() });
      }

      const jumpLevel = cueJumpLevel(payload);
      // BELL: just ring + the invalidations above. No need to fetch the copy.
      if (jumpLevel === 'BELL') {
        ring();
        return;
      }

      const notificationId = cueNotificationId(payload);
      void fetchNotifications({ limit: 10 })
        .then((page) => {
          // The just-created row is on the newest-first front page. If the cue
          // had no id (older relay shape), fall back to the newest unread row.
          const row =
            page.items.find((n) => n.id === notificationId) ??
            (notificationId === undefined && page.items[0]?.read === false ? page.items[0] : undefined);
          if (!row) return;
          if (jumpLevel === 'POPUP') {
            ring();
            openDetail(row);
            // The pop-up jumped in front of the user unprompted, so it counts as
            // seen — mark it read (mirrors the manual open/banner-click paths),
            // otherwise it lingers unread with no click coming to clear it.
            markReadRef.current(row);
          } else {
            // BANNER — showIncoming rings and pops the banner (skipping the
            // banner, but still ringing, when the panel is already open).
            showIncoming(row);
          }
        })
        .catch(() => {
          // Banner/pop-up are best-effort — the invalidations above keep state fresh.
        });
    };
    socket.on(NOTIFICATION_CREATED, onCreated);
    return () => {
      socket.off(NOTIFICATION_CREATED, onCreated);
    };
  }, [queryClient, fetchNotifications, ring, showIncoming, openDetail]);
}
